import { DateHelper, NameHelper } from "@swivl-mono/helpers";
import { Channel, EscalationInterface, EscalationStatus, SessionInterface, UserInterface } from "@swivl-mono/types";

import { Member } from "../../Member/Member.Current";
import { UserConversationsInterface, UsersModelStoreInterface } from "../Users.Model";

export class UsersModelSetDataHelpers {
  static isNewerThan<T extends { updatedAt: string | Date; version?: number }>(item: T, stateItem: T): boolean {
    if (!item) return false;
    if (!stateItem) return true;
    if (item.version && stateItem.version) {
      return item.version > stateItem.version;
    }
    return new Date(item.updatedAt).getTime() > new Date(stateItem.updatedAt).getTime();
  }

  static compileAllUsersSessionsAndEscalations = (
    state: UsersModelStoreInterface,
    users?: UserInterface[],
    sessions?: SessionInterface[],
    escalations?: EscalationInterface[]
  ) => {
    let dataArrays = {
      users: users || [],
      sessions: sessions || [],
      escalations: escalations || []
    };

    if (escalations) {
    }

    dataArrays.users = DateHelper.fixDates<UserInterface[]>(dataArrays.users);
    dataArrays.sessions = DateHelper.fixDates<SessionInterface[]>(dataArrays.sessions);
    dataArrays.escalations = DateHelper.fixDates<EscalationInterface[]>(dataArrays.escalations);
    if (escalations) {
    }
    // Add the state data to the arrays if they exist.
    // IMPORTANT: Clone objects to avoid mutating state references
    // IMPORTANT: State data must be PREPENDED so it's processed first, allowing newer
    // incoming data to inherit escalation associations from existing state
    if (state.users) {
      dataArrays.users = Object.values(state.users)
        .map((u) => ({ ...u }))
        .concat(dataArrays.users);
    }
    if (state.sessions) {
      dataArrays.sessions = Object.values(state.sessions)
        .map((s) => ({ ...s, escalation: s.escalation ? { ...s.escalation } : undefined }))
        .concat(dataArrays.sessions);
    }
    if (state.escalations) {
      dataArrays.escalations = Object.values(state.escalations)
        .map((e) => ({ ...e }))
        .concat(dataArrays.escalations);
    }

    for (const session of dataArrays.sessions) {
      if (session.user) {
        dataArrays.users.push({ ...session.user });
      }
      if (session.escalation) {
        dataArrays.escalations.push({ ...session.escalation });
      }
    }
    for (const escalation of dataArrays.escalations) {
      if (escalation.user) {
        dataArrays.users.push({ ...escalation.user });
      }
      if (escalation.session) {
        dataArrays.sessions.push({
          ...escalation.session,
          escalation: escalation.session.escalation ? { ...escalation.session.escalation } : undefined
        });
      }
    }
    let dataMap = {
      users: new Map<number, UserInterface>(),
      sessions: new Map<number, SessionInterface>(),
      escalations: new Map<number, EscalationInterface>()
    };
    for (const user of dataArrays.users) {
      const odUserId = Number(user.id);
      if (!dataMap.users.has(odUserId) || this.isNewerThan(user, dataMap.users.get(odUserId)!)) {
        dataMap.users.set(odUserId, { ...user });
      }
    }

    for (const escalation of dataArrays.escalations) {
      const odEscalationId = Number(escalation.id);
      if (!dataMap.escalations.has(odEscalationId) || this.isNewerThan(escalation, dataMap.escalations.get(odEscalationId)!)) {
        dataMap.escalations.set(odEscalationId, { ...escalation });
      }
    }
    console.log(`[COMPILE] Processing ${dataArrays.sessions.length} sessions, ${dataArrays.escalations.length} escalations`);
    for (const session of dataArrays.sessions) {
      const odSessionId = Number(session.id);
      const existingSession = dataMap.sessions.get(odSessionId);

      console.log(
        `[COMPILE] Session ${odSessionId}: existing=${!!existingSession}, isNewer=${!existingSession || this.isNewerThan(session, existingSession)}, session.escalation=${session.escalation?.id}, session.escalationId=${session.escalationId}`
      );

      if (!existingSession || this.isNewerThan(session, existingSession)) {
        // Create a clone of the session to avoid mutating the original
        const sessionCopy: SessionInterface = { ...session };

        // Determine the escalation ID to use - prefer the one on the session object,
        // then fall back to escalationId field, then fall back to existing session's escalation
        const escalationIdToLookup =
          sessionCopy.escalation?.id != null
            ? Number(sessionCopy.escalation.id)
            : sessionCopy.escalationId != null
              ? Number(sessionCopy.escalationId)
              : existingSession?.escalation?.id != null
                ? Number(existingSession.escalation.id)
                : existingSession?.escalationId != null
                  ? Number(existingSession.escalationId)
                  : undefined;

        // Try to look up the escalation from the map
        if (escalationIdToLookup && dataMap.escalations.has(escalationIdToLookup)) {
          const escalation = dataMap.escalations.get(escalationIdToLookup)!;
          if (escalation.resolved) {
            // Escalation is resolved, clear it from the session
            sessionCopy.escalation = undefined;
            sessionCopy.escalationId = undefined;
          } else {
            // Associate the escalation with the session
            sessionCopy.escalation = { ...escalation };
            sessionCopy.escalationId = escalation.id;
          }
        } else if (sessionCopy.escalation) {
          // Session has an escalation object but it's not in the map
          if (sessionCopy.escalation.resolved) {
            sessionCopy.escalation = undefined;
            sessionCopy.escalationId = undefined;
          } else {
            // Keep the escalation and ensure escalationId is set
            sessionCopy.escalationId = sessionCopy.escalation.id;
          }
        } else {
          // No escalation found - ensure fields are cleared
          sessionCopy.escalation = undefined;
          sessionCopy.escalationId = undefined;
        }

        console.log(
          `[COMPILE] Session ${odSessionId} stored with escalation=${sessionCopy.escalation?.id}, escalationId=${sessionCopy.escalationId}`
        );
        dataMap.sessions.set(odSessionId, sessionCopy);
      }
    }

    console.log(`[COMPILE] Final dataMap: ${dataMap.sessions.size} sessions, ${dataMap.escalations.size} escalations`);
    return dataMap;
  };

  static createUserConversationsFromDataMap(dataMap: ReturnType<typeof UsersModelSetDataHelpers.compileAllUsersSessionsAndEscalations>): {
    [userId: number]: UserConversationsInterface;
  } {
    let conversations: { [userId: number]: UserConversationsInterface } = {};
    for (const [userId, user] of dataMap.users.entries()) {
      if (user.isArchived) {
        // Skip archived users
        continue;
      }
      // Clone user to avoid shared references
      const userCopy = { ...user };
      if (!conversations[userId]) {
        conversations[userId] = {
          name: NameHelper.nameForUser(userCopy),
          user: userCopy,
          escalations: {},
          sessions: {}
        };
      } else {
        conversations[userId].user = userCopy; // Update user data if it exists (cloned)
        conversations[userId].name = NameHelper.nameForUser(userCopy);
      }
    }

    // Need to make sure no new escalations haven't come in that are not on the sessions.
    for (const [escalationId, escalation] of dataMap.escalations.entries()) {
      const escalationUserId = escalation.userId ? Number(escalation.userId) : undefined;
      const escalationSessionId = escalation.sessionId ? Number(escalation.sessionId) : undefined;

      console.log(`[ESC_LOOP] Processing escalation ${escalationId}, sessionId=${escalationSessionId}, resolved=${escalation.resolved}`);

      // Create a copy of escalation to avoid mutating the map entry directly
      const escalationCopy = { ...escalation };

      if (escalationUserId && dataMap.users.has(escalationUserId)) {
        escalationCopy.user = { ...dataMap.users.get(escalationUserId)! }; // Ensure escalation has user data (cloned)
      }

      // Update the escalation in the map with the enriched copy
      dataMap.escalations.set(escalationId, escalationCopy);

      if (escalationSessionId && dataMap.sessions.has(escalationSessionId)) {
        const session = dataMap.sessions.get(escalationSessionId)!;
        const existingEscalationId = session.escalation?.id != null ? Number(session.escalation.id) : undefined;
        console.log(
          `[ESC_LOOP] Found session ${escalationSessionId} for escalation ${escalationId}, session.escalation=${existingEscalationId}, session.escalationId=${session.escalationId}`
        );

        // Determine if we should update the session's escalation:
        // 1. Session has no escalation - use this one
        // 2. This escalation has a higher ID than the existing one (newer escalation supersedes older)
        // 3. Session's escalationId matches this escalation's ID - this is the correct escalation
        const shouldUpdate =
          !session.escalation ||
          (existingEscalationId != null && escalationId > existingEscalationId) ||
          Number(session.escalationId) === escalationId;

        console.log(
          `[ESC_LOOP] shouldUpdate=${shouldUpdate} (existingEscalationId=${existingEscalationId}, escalationId=${escalationId}, session.escalationId=${session.escalationId})`
        );

        if (shouldUpdate) {
          // Create a new session copy with the updated escalation
          const sessionCopy = {
            ...session,
            escalation: { ...escalationCopy },
            escalationId: escalationCopy.id
          };
          console.log(`[ESC_LOOP] Setting escalation ${escalationCopy.id} on session ${escalationSessionId}`);
          dataMap.sessions.set(escalationSessionId, sessionCopy); // Update session in the map
        }
      } else {
        console.log(`[ESC_LOOP] Session ${escalationSessionId} NOT found in dataMap.sessions for escalation ${escalationId}`);
      }
    }

    for (const [sessionId, session] of dataMap.sessions.entries()) {
      if (!session.open) {
        continue; // Skip closed sessions
      }
      const sessionUserId = session.userId ? Number(session.userId) : undefined;
      const sessionEscalationId = session.escalationId ? Number(session.escalationId) : undefined;

      console.log(
        `[SESS_LOOP] Processing session ${sessionId}, userId=${sessionUserId}, escalationId=${sessionEscalationId}, session.escalation=${session.escalation?.id}, channel=${session.channel}`
      );

      if (!sessionUserId) {
        continue; // Skip sessions without a userId
      }

      // Create a copy of the session to avoid mutating the map entry
      const sessionCopy: SessionInterface = { ...session };

      if (dataMap.users.has(sessionUserId)) {
        sessionCopy.user = { ...dataMap.users.get(sessionUserId)! }; // Ensure session has user data (cloned)
      }

      if (!conversations[sessionUserId]) {
        conversations[sessionUserId] = {
          name: NameHelper.nameForUser(sessionCopy.user),
          user: sessionCopy.user ? { ...sessionCopy.user } : undefined,
          escalations: {},
          sessions: {}
        };
      }

      // Try to find escalation by sessionEscalationId first, then fall back to session.escalation
      const escalationIdToUse = sessionEscalationId ?? (sessionCopy.escalation?.id ? Number(sessionCopy.escalation.id) : undefined);
      console.log(
        `[SESS_LOOP] Session ${sessionId}: escalationIdToUse=${escalationIdToUse}, inMap=${escalationIdToUse ? dataMap.escalations.has(escalationIdToUse) : false}`
      );

      if (escalationIdToUse && dataMap.escalations.has(escalationIdToUse)) {
        const escalation = dataMap.escalations.get(escalationIdToUse)!;
        console.log(`[SESS_LOOP] Session ${sessionId}: Found escalation ${escalationIdToUse}, resolved=${escalation.resolved}`);
        if (!escalation.resolved) {
          const escalationCopy = { ...escalation };
          conversations[sessionUserId].escalations![escalation.id] = escalationCopy;
          sessionCopy.escalation = escalationCopy; // Ensure session has escalation data
          sessionCopy.escalationId = escalation.id; // Ensure escalationId is set
          console.log(`[SESS_LOOP] Session ${sessionId}: Associated escalation ${escalation.id}`);
        } else {
          sessionCopy.escalation = undefined; // Ensure session has no escalation if resolved
          sessionCopy.escalationId = undefined; // Ensure escalationId is also cleared
          console.log(`[SESS_LOOP] Session ${sessionId}: Escalation ${escalationIdToUse} is resolved, clearing`);
        }
      } else if (sessionCopy.escalation && !sessionCopy.escalation.resolved) {
        // Session has escalation but it's not in the map - keep it and add to conversations
        const escalationCopy = { ...sessionCopy.escalation };
        conversations[sessionUserId].escalations![sessionCopy.escalation.id] = escalationCopy;
        sessionCopy.escalation = escalationCopy;
        sessionCopy.escalationId = sessionCopy.escalation.id; // Ensure escalationId is set
        console.log(`[SESS_LOOP] Session ${sessionId}: Kept existing escalation ${sessionCopy.escalation.id} (not in map)`);
      } else {
        sessionCopy.escalation = undefined; // Ensure session has no escalation if not found
        sessionCopy.escalationId = undefined; // Ensure escalationId is also cleared
        console.log(`[SESS_LOOP] Session ${sessionId}: No escalation found or resolved`);
      }

      console.log(
        `[SESS_LOOP] Final session ${sessionId} for conversation: escalation=${sessionCopy.escalation?.id}, escalationId=${sessionCopy.escalationId}`
      );
      conversations[sessionUserId].sessions![sessionCopy.channel] = sessionCopy;
    }

    for (const [userId, user] of dataMap.users.entries()) {
      if (user.isArchived) {
        //remove archived users from conversations
        delete conversations[userId];
        continue;
      }
    }

    return conversations;
  }

  static mappedFromObjects<T>(map: Map<number, T>): { [id: number]: T } {
    const obj: { [id: number]: T } = {};
    map.forEach((value, key) => {
      obj[key] = value;
    });
    return obj;
  }

  static getUnacknowledgedEscalations(escalations: { [escalationId: number]: EscalationInterface } | undefined): EscalationInterface[] {
    if (!escalations) return [];
    let items = Object.values(escalations).filter((e) => {
      if (e.membersAttemptedIds?.includes(Member.id!) && !e.acknowledgedMemberIds?.includes(Member.id!)) {
        return true; // Already attempted by the current member
      }
      return false;
    });

    return items.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }

  static removeDeletedOrgMergedUsersFromConversations(state: UsersModelStoreInterface): UsersModelStoreInterface {
    let userIdsToRemove: number[] = [];
    for (const [_, user] of Object.entries(state.users || {})) {
      if (user && (user as any).deletedDate) {
        userIdsToRemove.push(user.id);
      }
    }
    for (const [_, session] of Object.entries(state.sessions || {})) {
      if (session && session.merged && session.merged.from) {
        userIdsToRemove.push(session.merged.from);
      }
    }

    if (userIdsToRemove.length) {
      for (const id of userIdsToRemove) {
        if (state.conversations && state.conversations[id]) {
          delete state.conversations[id];
        }
        if (state.users && state.users[id]) {
          delete state.users[id];
        }
        // Also remove any sessions associated with this user
        if (state.sessions) {
          for (const [sessionId, session] of Object.entries(state.sessions)) {
            if (session.userId === id) {
              delete state.sessions[Number(sessionId)];
            }
          }
        }
      }
    }

    return state;
  }

  static setLastMessageForConversations(_state: UsersModelStoreInterface): UsersModelStoreInterface {
    if (!_state.conversations) {
      return _state;
    }
    const state = { ..._state };
    for (const [userId, conversation] of Object.entries(state.conversations)) {
      let lastUserMessageAt: Date | undefined;
      let lastUserMessageChannel: Channel | undefined;
      let lastUserMessage: string | undefined;
      const user = state.users ? state.users[Number(userId)] : undefined;
      if (user && user.lastUserMessageAt) {
        console.log("user :", user);

        lastUserMessageAt = new Date(user.lastUserMessageAt);
        lastUserMessageChannel = user.lastUserMessageChannel;
        lastUserMessage = user.lastUserMessage;
      }

      state.conversations[userId].lastUserMessageChannel = lastUserMessageChannel;
      state.conversations[userId].lastUserMessageAt = lastUserMessageAt;
      state.conversations[userId].lastUserMessage = lastUserMessage || "";
    }

    // for (const [userId, conversation] of Object.entries(state.conversations)) {
    //   for (const [_, session] of Object.entries(conversation.sessions || {})) {
    //     if (session.channel === Channel.VOICE && session.open && session.escalationId) {
    //       // If there is an open voice session with an escalation, prioritize that
    //       lastUserMessageAt = new Date(); // Set to now since the call is active
    //       lastUserMessageChannel = Channel.VOICE;
    //       lastUserMessage = session.lastUserMessage || "On Call";
    //       break; // No need to check further sessions
    //     }
    //     if (session.lastUserMessageAt) {
    //       if (!lastUserMessageAt || new Date(session.lastUserMessageAt) > new Date(lastUserMessageAt)) {
    //         if (session.channel === Channel.VOICE && !session.lastUserMessageAt) {
    //           lastUserMessageAt = new Date(session.createdAt);
    //         } else {
    //           lastUserMessageAt = new Date(session.lastUserMessageAt);
    //         }

    //         lastUserMessageChannel = session.channel;
    //         lastUserMessage = session.lastUserMessage;
    //       }
    //     }
    //   }

    //   if (!lastUserMessageAt) {
    //     if (user && user.lastUserMessageAt) {
    //       lastUserMessageAt = new Date(user.lastUserMessageAt);
    //     } else if (user && user.createdAt) {
    //       lastUserMessageAt = new Date(user.createdAt);
    //     }
    //   }

    //   if (!lastUserMessageChannel) {
    //     let user = state.users ? state.users[Number(userId)] : undefined;
    //     if (user && user.channels && user.channels.length > 0) {
    //       lastUserMessageChannel = user.channels[0];
    //     } else {
    //       lastUserMessageChannel = Channel.WEB;
    //     }
    //   }
    // }

    return state;
  }
}
